import { FlexBox } from '@ardoq/layout';
import { colors, space } from '@ardoq/design-tokens';
import {
  DragButton,
  Icon,
  IconName,
  IconSize,
  MinimalIconProps,
} from '@ardoq/icons';
import {
  DragAndDropContextState,
  DragAndDropSectionsProvider,
  isTargetDragHandleButton,
  useDragAndDropContext,
} from '@ardoq/drag-and-drop';
import {
  DropdownContainer,
  DropdownDivider,
  DropdownMaxHeight,
  DropdownMenu,
  DropdownOption,
} from './Dropdown2026';
import { Link, Text } from '@ardoq/typography';
import { FormSize, TextInput } from '@ardoq/forms';
import { DragEvent, PropsWithChildren } from 'react';
import { styled } from 'styled-components';

export type ColumnSelectMenuLabels = {
  visibilityHeader: string;
  chooseColumnsHeader: string;
  searchPlaceholder: string;
  shownInTableHeader: string;
  hideAllAction: string;
  hiddenInTableHeader: string;
  showAllAction: string;
  hideColumnAriaLabel: (columnLabel: string) => string;
  showColumnAriaLabel: (columnLabel: string) => string;
};

export const defaultColumnSelectMenuLabels: ColumnSelectMenuLabels = {
  visibilityHeader: 'COLUMN VISIBILITY',
  chooseColumnsHeader: 'CHOOSE COLUMNS',
  searchPlaceholder: 'Filter columns',
  shownInTableHeader: 'Shown in table',
  hideAllAction: 'Hide all',
  hiddenInTableHeader: 'Hidden in table',
  showAllAction: 'Show all',
  hideColumnAriaLabel: () => 'Hide column',
  showColumnAriaLabel: () => 'Show column',
};

export type ColumnSelectMenuProps = {
  type: 'column-select';

  search: {
    value: string;
    onValueChange: (value: string) => void;
    /**
     * @deprecated Pass via `labels.searchPlaceholder` instead.
     */
    placeholder?: string;
  };

  selectedColumns: ColumnMenuItem[];
  deselectedColumns: ColumnMenuItem[];
  onColumnReorder: (
    newIdOrder: string[],
    dragAndDropState: DragAndDropContextState
  ) => void;
  isReorderDisabled?: boolean;
  dragSectionsSeparatorId: string;

  onSelectAll: VoidFunction;
  onDeselectAll: VoidFunction;

  labels?: Partial<ColumnSelectMenuLabels>;
};

export const ColumnSelectMenu = (props: ColumnSelectMenuProps) => {
  const labels: ColumnSelectMenuLabels = {
    ...defaultColumnSelectMenuLabels,
    ...props.labels,
  };
  return (
    <DropdownContainer dropdownMaxHeight={DropdownMaxHeight.LARGE}>
      <StyledDropdownMenu>
        <DragAndDropSectionsProvider
          onDrop={props.isReorderDisabled ? undefined : props.onColumnReorder}
        >
          {[
            <PaddedFlexbox key="header">
              <Text variant="captionBold" color="textSubtle">
                {labels.visibilityHeader}
              </Text>
            </PaddedFlexbox>,
            <DropdownDivider key="header-divider" />,
            <PaddedFlexbox key="search-label">
              <Text variant="captionBold" color="textSubtle">
                {labels.chooseColumnsHeader}
              </Text>
            </PaddedFlexbox>,
            <PaddedFlexbox key="search">
              <TextInput
                value={props.search.value}
                onValueChange={props.search.onValueChange}
                placeholder={
                  props.search.placeholder ?? labels.searchPlaceholder
                }
                // TODO - currently as of writing, there's a bug with Popover2025
                // which results in this `clear` icon click to trigger the
                // `onClickOutside`, closing the popover(s).
                // onClear={() => props.search.onValueChange('')}
                formSize={FormSize.FULL}
              />
            </PaddedFlexbox>,
            <PaddedFlexbox
              key="subheader-shown-in-table"
              justify="space-between"
            >
              <Text variant="text2" color="textSubtle">
                {labels.shownInTableHeader}
              </Text>
              <Link typography="text2" hideIcon onClick={props.onDeselectAll}>
                {labels.hideAllAction}
              </Link>
            </PaddedFlexbox>,
            ...props.selectedColumns.map(column => (
              <ColumnOption
                key={column.id}
                dragAndDropCardId={column.id}
                column={column}
                isReorderDisabled={props.isReorderDisabled}
                hideColumnAriaLabel={labels.hideColumnAriaLabel}
                showColumnAriaLabel={labels.showColumnAriaLabel}
              />
            )),
            <DragSectionSeparatorWrapper
              key="drag-section-separator"
              dragAndDropCardId={props.dragSectionsSeparatorId}
            >
              <DropdownDivider key="section-divider" />
              <PaddedFlexbox
                key="subheader-hidden-in-table"
                justify="space-between"
              >
                <Text variant="text2" color="textSubtle">
                  {labels.hiddenInTableHeader}
                </Text>
                <Link typography="text2" hideIcon onClick={props.onSelectAll}>
                  {labels.showAllAction}
                </Link>
              </PaddedFlexbox>
            </DragSectionSeparatorWrapper>,
            ...props.deselectedColumns.map(column => (
              <ColumnOption
                key={column.id}
                dragAndDropCardId={column.id}
                column={column}
                isReorderDisabled={props.isReorderDisabled}
                hideColumnAriaLabel={labels.hideColumnAriaLabel}
                showColumnAriaLabel={labels.showColumnAriaLabel}
              />
            )),
          ]}
        </DragAndDropSectionsProvider>
      </StyledDropdownMenu>
    </DropdownContainer>
  );
};

const StyledDropdownMenu = styled(DropdownMenu)`
  gap: ${space.xsmall};
`;

const PaddedFlexbox = styled(FlexBox)`
  padding: ${space.small} ${space.medium} 0 ${space.medium};
`;

const DragSectionSeparatorWrapper = (
  props: PropsWithChildren<{ dragAndDropCardId: string }>
) => {
  // To allow dragging items between the sections, we need to add the
  // `onDragOver` property, using `setDraggedOverCard` from the drag
  // and drop context
  const { setDraggedOverCard } = useDragAndDropContext();
  return (
    <FlexBox
      flexDirection="column"
      onDragOver={(e: DragEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const middle = rect.top + rect.height / 2;
        setDraggedOverCard(
          props.dragAndDropCardId,
          middle > e.clientY ? 'before' : 'after'
        );
      }}
    >
      {props.children}
    </FlexBox>
  );
};

export type ColumnMenuItem = {
  id: string;
  label: string;

  isSelected: boolean;
  onSelect: VoidFunction;

  isDisabled?: boolean;
  // when disabled, icons can be shown on the item
  icon?: MinimalIconProps;
};

type ColumnOptionProps = {
  column: ColumnMenuItem;
  // `dragAndDropCardId` needs to be a prop, because
  // that's how `DragAndDropSectionsProvider` knows
  // its draggable
  dragAndDropCardId: string;
  isReorderDisabled?: boolean;
  hideColumnAriaLabel: (columnLabel: string) => string;
  showColumnAriaLabel: (columnLabel: string) => string;
};

const ColumnOption = ({
  column,
  dragAndDropCardId,
  isReorderDisabled,
  hideColumnAriaLabel,
  showColumnAriaLabel,
}: ColumnOptionProps) => {
  const { setDraggedCardId, setDraggedOverCard, resetState } =
    useDragAndDropContext();
  const isDraggable = !column.isDisabled && !isReorderDisabled;
  return (
    <StyledDropdownOption
      isDisabled={column.isDisabled}
      onClick={column.onSelect}
      draggable={isDraggable}
      onDragStart={e => {
        if (!isDraggable || !isTargetDragHandleButton(e.target)) return;
        e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
        setDraggedCardId(dragAndDropCardId);
      }}
      onDragOver={e => {
        if (!isDraggable) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const middle = rect.top + rect.height / 2;
        setDraggedOverCard(
          dragAndDropCardId,
          middle > e.clientY ? 'before' : 'after'
        );
      }}
      onDragEnd={resetState}
    >
      <FlexBox align="center" gap="small">
        {isDraggable ? (
          <DragButton draggable iconSize={IconSize.MEDIUM} />
        ) : column.icon ? (
          <Icon
            iconName={column.icon.name}
            color={column.icon.color ?? colors.icon}
            iconSize={IconSize.MEDIUM}
          />
        ) : (
          <EmptyIconSpacer />
        )}
        {column.label}
        <FlexBox marginLeft="auto">
          <Icon
            iconName={
              column.isSelected ? IconName.VISIBILITY : IconName.VISIBILITY_OFF
            }
            color={
              column.isDisabled ? colors.iconActionDisabled : colors.iconAction
            }
            iconSize={IconSize.MEDIUM}
            aria-label={
              column.isSelected
                ? hideColumnAriaLabel(column.label)
                : showColumnAriaLabel(column.label)
            }
          />
        </FlexBox>
      </FlexBox>
    </StyledDropdownOption>
  );
};

const StyledDropdownOption = styled(DropdownOption)`
  &[aria-disabled='true'] {
    /* remove 'DropdownOption' disabled background */
    background-color: ${colors.surfaceDefault};
  }
`;

const EmptyIconSpacer = styled.span`
  display: inline-block;
  width: ${IconSize.MEDIUM}px;
  height: ${IconSize.MEDIUM}px;
  flex-shrink: 0;
`;
